import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { PushNotificationService } from '../../services/push-notification.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-push-notification-manager',
  templateUrl: './push-notification-manager.component.html',
  styleUrls: ['./push-notification-manager.component.scss']
})
export class PushNotificationManagerComponent implements OnInit, OnDestroy {
  isSubscribed = false;
  isSupported = false;
  permission: NotificationPermission = 'default';
  subscription: PushSubscription | null = null;
  autoSubscribeEnabled = true;
  notificationPreferences: any = null;
  subscriptionStatus: any = null;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private pushService: PushNotificationService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.checkSupport();
    this.checkPermission();
    this.checkSubscriptionStatus();
    this.loadNotificationPreferences();
    this.loadSubscriptionStatus();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private checkSupport(): void {
    this.isSupported = this.pushService.isEnabled;
  }

  private checkPermission(): void {
    this.permission = this.pushService.checkPermission();
  }

  private checkSubscriptionStatus(): void {
    const sub = this.pushService.subscription.subscribe(subscription => {
      this.subscription = subscription;
      this.isSubscribed = !!subscription;
    });
    this.subscriptions.add(sub);
  }

  private loadNotificationPreferences(): void {
    this.notificationPreferences = this.pushService.getNotificationPreferences();
    this.autoSubscribeEnabled = this.notificationPreferences.autoSubscribe;
  }

  private loadSubscriptionStatus(): void {
    this.subscriptionStatus = this.authService.getPushNotificationSubscriptionStatus();
  }

  onAutoSubscribeChange(enabled: boolean): void {
    this.autoSubscribeEnabled = enabled;
    this.pushService.saveNotificationPreferences(true, enabled);
    console.log('Auto-subscribe preference changed:', enabled);
  }

  async getDetailedStatus(): Promise<void> {
    try {
      const status = await this.pushService.getSubscriptionStatus();
      console.log('Detailed subscription status:', status);
      this.notificationPreferences = status.preferences;
    } catch (error) {
      console.error('Error getting detailed status:', error);
    }
  }

  async requestPermission(): Promise<void> {
    try {
      this.permission = await this.pushService.requestPermission();
      if (this.permission === 'granted') {
        console.log('Permission granted');
      } else {
        console.log('Permission denied');
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
    }
  }

  async subscribe(): Promise<void> {
    try {
      console.log('🔔 Manual subscription requested');

      if (this.permission !== 'granted') {
        await this.requestPermission();
      }

      if (this.permission === 'granted') {
        console.log('📡 Subscribing to push notifications...');
        const subscription = await this.pushService.requestSubscription();

        if (subscription) {
          this.subscription = subscription;
          this.isSubscribed = true;

          console.log('✅ Successfully subscribed locally');
          console.log('🚀 Sending subscription to server...');

          // Send to server with current user
          const currentUser = this.authService.getUserName();
          const serverResult = await this.pushService.sendSubscriptionToServer(subscription, currentUser);

          if (serverResult) {
            console.log('✅ Subscription sent to server successfully');

            // Update local status
            localStorage.setItem('pushNotificationStatus', JSON.stringify({
              subscribed: true,
              subscribedAt: new Date().toISOString(),
              userId: currentUser,
              endpoint: subscription.endpoint,
              manualSubscription: true
            }));

            this.loadSubscriptionStatus();
          } else {
            console.warn('⚠️ Local subscription active, but server sync failed');
          }

          console.log('🎉 Manual subscription completed successfully');
        }
      }
    } catch (error) {
      console.error('❌ Error during manual subscription:', error);
    }
  }

  async unsubscribe(): Promise<void> {
    try {
      console.log('🔕 Manual unsubscribe requested');

      if (this.subscription) {
        // Remove from server first
        const currentUser = this.authService.getUserName();
        console.log('🗑️ Removing subscription from server...');

        const serverResult = await this.pushService.removeSubscriptionFromServer(this.subscription, currentUser);

        if (serverResult) {
          console.log('✅ Subscription removed from server');
        } else {
          console.warn('⚠️ Failed to remove from server, proceeding with local unsubscribe');
        }
      }

      // Unsubscribe locally
      const success = await this.pushService.unsubscribe();

      if (success) {
        this.subscription = null;
        this.isSubscribed = false;

        // Clear local status
        localStorage.removeItem('pushNotificationStatus');
        this.subscriptionStatus = null;

        console.log('✅ Successfully unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('❌ Error during unsubscribe:', error);
    }
  }

  testNotification(): void {
    this.pushService.showLocalNotification({
      title: 'Test Notification',
      body: 'This is a test push notification from Builtgenix!',
      icon: '/assets/icons/icon-192x192.png',
      data: { url: '/' }
    });
  }

  initializePushNotifications(): void {
    this.pushService.initializePushNotifications();
  }

  get statusText(): string {
    if (!this.isSupported) {
      return 'Push notifications are not supported in this browser';
    }

    switch (this.permission) {
      case 'granted':
        return this.isSubscribed ? 'Subscribed to push notifications' : 'Permission granted, click Subscribe';
      case 'denied':
        return 'Push notifications are blocked. Please enable them in browser settings.';
      case 'default':
        return 'Click "Request Permission" to enable push notifications';
      default:
        return 'Unknown permission status';
    }
  }

  get subscriptionInfo(): string {
    if (!this.subscription) return 'No subscription';

    const p256dhKey = this.subscription.getKey ? this.subscription.getKey('p256dh') : null;
    const authKey = this.subscription.getKey ? this.subscription.getKey('auth') : null;

    return JSON.stringify({
      endpoint: this.subscription.endpoint,
      p256dh: p256dhKey ? btoa(String.fromCharCode(...new Uint8Array(p256dhKey))) : 'Not available',
      auth: authKey ? btoa(String.fromCharCode(...new Uint8Array(authKey))) : 'Not available'
    }, null, 2);
  }

  getCurrentSubscriptionStatus(): string {
    if (!this.subscriptionStatus) {
      return 'Not subscribed';
    }

    const status = this.subscriptionStatus;
    const subscribedAt = new Date(status.subscribedAt).toLocaleString();
    const type = status.manualSubscription ? 'Manual' : 'Auto';

    return `${type} subscription since ${subscribedAt}`;
  }
}
